import { CheckCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Collapse, Descriptions, Space, Tag, Typography, message } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { programsActions } from "@fitness-app/app-store";
import { type TrainingProgramProposal } from "@fitness-app/data-models/entities/TrainingProgramAgent";

import { getClientWebApiBaseUrl } from "~/modules/Sales/SalesAgent/constants";
import { supabase, useAppDispatch } from "~/store/initializeStore";

const weekdays: Record<string, string> = {
  monday: "Poniedziałek",
  tuesday: "Wtorek",
  wednesday: "Środa",
  thursday: "Czwartek",
  friday: "Piątek",
  saturday: "Sobota",
  sunday: "Niedziela",
};
const scheduleWeekLabels = ["A", "B"] as const;

export const TrainingProgramProposalCard = ({ proposal }: { proposal: TrainingProgramProposal }) => {
  const [saving, setSaving] = useState(false);
  const [savedProgramId, setSavedProgramId] = useState<string | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workoutNames = new Map(proposal.draft.workouts.map((workout) => [workout.workoutKey, workout.name]));

  const save = async () => {
    if (!proposal.canSave || saving) return;
    setSaving(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const response = await fetch(`${getClientWebApiBaseUrl()}/api/trainers/ai/training-program-save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ draft: proposal.draft }),
      });
      const result = (await response.json()) as { error?: string; programId?: string };
      if (!response.ok || !result.programId) throw new Error(result.error || "Nie udało się zapisać planu");
      setSavedProgramId(result.programId);
      void dispatch(programsActions.fetchPrograms());
      void message.success("Szablon planu został zapisany");
    } catch (error) {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać planu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      size="small"
      className="mt-3 border-blue-200 bg-blue-50/40"
      title={
        <Space>
          {proposal.canSave ? (
            <CheckCircleOutlined className="text-emerald-600" />
          ) : (
            <ExclamationCircleOutlined className="text-amber-600" />
          )}
          <span>Draft: {proposal.draft.name}</span>
        </Space>
      }
    >
      <Descriptions size="small" column={2} className="mb-3">
        <Descriptions.Item label="Poziom">{proposal.draft.level}</Descriptions.Item>
        <Descriptions.Item label="Typ">{proposal.draft.type}</Descriptions.Item>
        <Descriptions.Item label="Treningi">{proposal.draft.workouts.length}</Descriptions.Item>
        <Descriptions.Item label="Tagi">{proposal.draft.tags.join(", ") || "—"}</Descriptions.Item>
      </Descriptions>

      <Collapse
        size="small"
        items={proposal.draft.workouts.map((workout) => ({
          key: workout.workoutKey,
          label: `${workout.name} (${workout.preWorkout.length + workout.exercises.length + workout.postWorkout.length} ćwiczeń)`,
          children: (
            <div className="space-y-2">
              {[
                ["Rozgrzewka", workout.preWorkout],
                ["Trening", workout.exercises],
                ["Schłodzenie", workout.postWorkout],
              ].map(([label, exercises]) =>
                (exercises as typeof workout.exercises).length ? (
                  <div key={label as string}>
                    <Typography.Text strong>{label as string}</Typography.Text>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(exercises as typeof workout.exercises).map((exercise) => (
                        <Tag
                          key={`${workout.workoutKey}-${label}-${exercise.exerciseId ?? exercise.requestedName}-${exercise.groupId ?? "single"}-${exercise.numberOfSeries}`}
                          color={exercise.resolutionStatus === "matched" ? "default" : "warning"}
                        >
                          {exercise.requestedName} · {exercise.numberOfSeries} serii
                        </Tag>
                      ))}
                    </div>
                  </div>
                ) : null,
              )}
            </div>
          ),
        }))}
      />

      {proposal.draft.defaultSchedule ? (
        <div className="mt-3">
          <Typography.Text strong>Domyślny harmonogram</Typography.Text>
          {proposal.draft.defaultSchedule.map((week, index) => {
            const weekLabel = scheduleWeekLabels[index] ?? String(index + 1);

            return (
              <div key={weekLabel} className="mt-1 flex flex-wrap gap-1">
                {Object.entries(week)
                  .filter(([, value]) => value.workoutDay)
                  .map(([day, value]) => (
                    <Tag color="blue" key={`${weekLabel}-${day}`}>
                      {weekdays[day]}: {workoutNames.get(value.workoutDay!) ?? value.workoutDay}
                    </Tag>
                  ))}
              </div>
            );
          })}
        </div>
      ) : null}

      {proposal.issues.length ? (
        <Alert
          className="mt-3"
          type="warning"
          showIcon
          message="Draft wymaga uzupełnienia"
          description={proposal.issues.join(" ")}
        />
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        {savedProgramId ? (
          <Button type="primary" onClick={() => navigate(`/programs/plans/${savedProgramId}`)}>
            Otwórz plan
          </Button>
        ) : (
          <Button type="primary" disabled={!proposal.canSave} loading={saving} onClick={() => void save()}>
            Zapisz jako szablon
          </Button>
        )}
      </div>
    </Card>
  );
};
